import React, { useEffect, useState } from 'react'
import { Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router'
import ErrorIcon from '@mui/icons-material/ErrorOutlineOutlined'
import { datadogRum } from '@datadog/browser-rum'

import {
	trackAnalyticEvent,
	convertSnakeCaseToTitleCase,
	responseModalConfigs,
	isValidErrorType,
	ANALYTICS_LOCATIONS, LANDING_ROUTE,
	queryClient,
	formatFeedInput,
	normalizePayloadString,
	sortRequiredFieldsLogically,
	removeExpiresAtFromRequiredFields,
	ACTIVE_FEEDS_QUERY_VARIABLES,
} from '../../utils'
import { TextInput } from '../text-input/text-input'
import { useModal, useToast } from '../../providers'
import { GetFeedQuery, RequiredFieldWithValue, useGetFeedQuery, useUpdateFeedMutation, useSearchFeedsQuery } from '../../graphql'
import { ActiveFeedStatus } from '../../types'
import { OAuthIntegrationManage } from '../oauth-integration/oauth-integration-manage/oauth-integration-manage'
import { BackLink, DeleteButtonContainer, DeleteWarningText, FooterButtonContainer, FooterContainer, FormInputContainer, LoadingContainer, StyledButton, StyledCredentialsContainer, StyledDeleteButton, StyledEditIcon, StyledForm, StyledHeaderTypography } from './manage-form.styles'

export const PROTECTED_FIELD_MASK = '**************'

type Feed = NonNullable<NonNullable<GetFeedQuery['feed']>['feed']>

export type SubmitProps = {
	[key: string]: string | undefined | null,
}

export type ManageFormProps = {
	isFormDirty: boolean,
	setIsFormDirty: React.Dispatch<React.SetStateAction<boolean>>,
}

export const ManageForm: React.FC<ManageFormProps> = ( { isFormDirty, setIsFormDirty } ) => {
	const params = useParams()
	const [ isFeedDescriptionEditable, setIsFeedDescriptionEditable ] = useState<boolean>( false )
	const [ isFeedCredentialsEditable, setIsFeedCredentialsEditable ] = useState<boolean>( false )
	const [ feedDescriptionError, setFeedDescriptionError ] = useState( false )

	const navigate = useNavigate()

	const initialFormDataRef = React.useRef<SubmitProps | null>( null )
	const buildInitialFormData = React.useCallback( ( feed: Feed ): SubmitProps => {
		const initialFieldValues: SubmitProps = {}
		const requiredFields = feed.integration?.requiredFields ?? []
		requiredFields.forEach( ( field ) => {
			if ( field.protected ) {
				return
			}
			initialFieldValues[ field.fieldName ] = field.currentValue ?? ''
		} )
		return {
			id: params.id,
			name: feed.name,
			...initialFieldValues,
		}
	}, [ params.id ] )

	const [ formData, setFormData ] = useState<SubmitProps>( {} )

	const { data: feed, isLoading: isLoadingFeedData } = useGetFeedQuery<Feed | null>(
		{ feedId: params.id ?? '' },
		{
			select: ( data ) => data.feed?.feed ?? null,
		},
	)

	const feedName = feed?.name
	const feedId = feed?.feedId
	const currentStatus = feed?.currentStatus
	const integration = feed?.integration
	const requiredFields = integration?.requiredFields
	const authType = integration?.authType
	const integrationName = integration?.name

	const { showModal, hideModal } = useModal()
	const { showToast } = useToast()
	const { mutateAsync: updateFeed } = useUpdateFeedMutation()

	const isOauth = authType === 'oauth2'

	const onFeedDescriptionChange = ( value: string ) => {
		setFeedDescriptionError( value.length > 32 )
		onFormChange( { ...formData, name: value } )
		setIsFormDirty( true )
	}

	const onFormChange = ( data: SubmitProps ) => {
		setIsFormDirty( true )
		setFormData( data )
	}

	useEffect( () => {
		if ( feed && !initialFormDataRef.current ) {
			const initialFormData = buildInitialFormData( feed )
			initialFormDataRef.current = initialFormData
			setFormData( initialFormData )
		}
	}, [ feed, buildInitialFormData ] )
	// show unsaved changes error in browser
	useEffect( () => {
		const handleBeforeUnload = ( e: BeforeUnloadEvent ) => {
			if ( isFormDirty ) {
				e.preventDefault()
				e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
				return 'You have unsaved changes. Are you sure you want to leave?'
			} else {
				return undefined
			}
		}

		window.addEventListener( 'beforeunload', handleBeforeUnload )

		return () => {
			window.removeEventListener( 'beforeunload', handleBeforeUnload )
		}
	}, [ isFormDirty ] )

	const onDeletePress = () => {
		if ( !feedId ) {
			return
		}
		trackAnalyticEvent(
			ANALYTICS_LOCATIONS.MANAGE_PAGE,
			'Click',
			'Delete Integration',
			{ name: integrationName ?? undefined },
		)
		const onDeleteConfirm = async() => {
			const response = await updateFeed( { input: { feedId, isDeleted: true } } )
			if ( response.updateFeed?.error ) {
				showModal( {
					title: 'Error Deleting Integration',
					headerText: 'There was an error deleting the integration',
					content: response.updateFeed.error.message,
				} )
				trackAnalyticEvent(
					ANALYTICS_LOCATIONS.MANAGE_PAGE,
					'Error',
					'Delete Integration',
					{ errorTitle: 'Error Deleting Integration', message: response.updateFeed.error.message },
				)
				return
			}
			queryClient.invalidateQueries( useSearchFeedsQuery.getKey( ACTIVE_FEEDS_QUERY_VARIABLES ) )
			hideModal()
			trackAnalyticEvent(
				ANALYTICS_LOCATIONS.MANAGE_PAGE,
				'Success',
				'Delete Integration',
				{ name: integrationName ?? undefined },
			)
			showToast( { title: 'Integration Deleted', message: 'Your integration was successfully deleted', type: 'success' } )
			navigate( LANDING_ROUTE )
		}
		showModal( {
			title: 'Delete Integration',
			icon: <ErrorIcon color="error" />,
			headerText: 'Delete Integration?',
			contentComponent: (
				<><DeleteWarningText>
					Deleting this integration will remove the AEMP feed connection, and telematics data will no longer update.
				</DeleteWarningText>
				<DeleteWarningText paddingTop={2}>
					This <strong>will not</strong> delete trackers or remove tracker assignments from assets. You&apos;ll need to reassign any replacement trackers to assets separately.
				</DeleteWarningText></>
			),
			buttons: [
				{ label: 'Cancel', variant: 'text', color: 'primary', onClick: () => hideModal() },
				{ label: 'Delete',
					variant: 'contained',
					color: 'error',
					onClick: onDeleteConfirm,
				},
			],
		} )
	}

	const isLoading = isLoadingFeedData

	if ( isLoading ) {
		// show a mui spinner icon in the left
		return (
			<LoadingContainer>
				<CircularProgress />
			</LoadingContainer>
		)
	}
	const renderDescriptionTextInput = () => {
		return (
			<TextInput
				name="name"
				label="Feed Description"
				disabled={!isFeedDescriptionEditable}
				value={formData.name ?? ''}
				onBlur={() => onFeedDescriptionChange( formData.name?.trim() ?? '' )}
				onChange={ ( e ) => onFeedDescriptionChange( e.target.value ) }
				error={feedDescriptionError}
				errorText={feedDescriptionError ? 'Description must be 32 characters or less' : undefined}
			/>
		)
	}

	const renderField = ( field: RequiredFieldWithValue ) => {
		const { fieldName, currentValue, description, protected: isProtected } = field
		const displayName = convertSnakeCaseToTitleCase( fieldName )
		const value = isProtected
			? ( isFeedCredentialsEditable ? ( formData[ fieldName ] ?? '' ) : PROTECTED_FIELD_MASK )
			: ( formData[ fieldName ] ?? currentValue ?? '' )

		return (
			<TextInput
				key={fieldName}
				name={fieldName}
				label={displayName}
				description={description}
				disabled={!isFeedCredentialsEditable}
				isMasked={isProtected}
				value={value}
				placeholder={isProtected ? '(Leave blank to leave unchanged)' : ''}
				hideMaskedFields={!isFeedCredentialsEditable && isProtected}
				onChange={ ( e ) => onFormChange( { ...formData, [ fieldName ]: e.target.value } ) }
			/>
		)
	}

	const shouldShowEditFeedDescriptionButton = !isFeedDescriptionEditable && !isFeedCredentialsEditable
	const isConnected = currentStatus !== ActiveFeedStatus.AUTH_FAILED
	const shouldShowEditFeedCredentialsButton = isOauth ? ( isConnected && !isFeedCredentialsEditable && !isFeedDescriptionEditable ) : !isFeedCredentialsEditable && !isFeedDescriptionEditable

	const shouldShowSubmitFeedCredentialsButton = isFeedCredentialsEditable && !isOauth
	const shouldShowSubmitFeedDescriptionButton = isFeedDescriptionEditable || shouldShowSubmitFeedCredentialsButton

	const onEditCredentialsClick = () => {
		setIsFeedCredentialsEditable( true )
		if ( !requiredFields?.length ) {
			return
		}
		setFormData( ( prev ) => {
			const next = { ...prev }
			requiredFields.forEach( ( field ) => {
				if ( field.protected ) {
					next[ field.fieldName ] = ''
				}
			} )
			return next
		} )
	}

	const onSubmitCredentials = async() => {
		const submitData: SubmitProps = { ...formData }
		const { name } = formData

		// Set name to null so it doesn't get updated
		requiredFields?.forEach( ( field ) => {
			if ( !field.protected ) {
				return
			}
			const value = submitData[ field.fieldName ]
			if ( value === null || value === undefined || value === '' || value === PROTECTED_FIELD_MASK || value === field.currentValue ) {
				submitData[ field.fieldName ] = null
			}
		} )

		if ( !integration?.requiredFields.length ) {
			return
		}

		const feedPayload = { input: {
			feedId: feedId!,
			verifyCredentials: true,
			name: normalizePayloadString( name ),
			...formatFeedInput( { ...submitData, name: undefined, id: undefined }, integration.requiredFields ),
		} }

		try {
			const response = await updateFeed( feedPayload )

			if ( response.updateFeed?.error ) {
				const errorType = response.updateFeed.error.type
				const modalConfig = isValidErrorType( errorType ) ? responseModalConfigs[ errorType ] : responseModalConfigs.ERROR

				showModal( modalConfig )

				trackAnalyticEvent(
					ANALYTICS_LOCATIONS.MANAGE_PAGE,
					'Error',
					'Submit Credentials',
					{ errorTitle: modalConfig.title },
				)
			} else if ( response.updateFeed?.success && !response.updateFeed.error ) {
				queryClient.invalidateQueries( useSearchFeedsQuery.getKey( ACTIVE_FEEDS_QUERY_VARIABLES ) )
				showToast( { title: 'Credentials Saved', message: 'We\'ll now get tracker feeds from your OEM', type: 'success' } )
				setIsFeedCredentialsEditable( false )
				setIsFormDirty( false )
				setIsFeedDescriptionEditable( false )
				queryClient.invalidateQueries( useGetFeedQuery.getKey( { feedId: feedId! } ) )
				trackAnalyticEvent(
					ANALYTICS_LOCATIONS.MANAGE_PAGE,
					'Success',
					'Edit Credentials',
					{ name },
				)
			}
		} catch ( error ) {
			showModal( responseModalConfigs.GENERIC_ERROR )
			datadogRum.addError( error as Error, { source: 'manage-form', name: 'Submit Credentials Error', payload: feedPayload } )
			trackAnalyticEvent(
				ANALYTICS_LOCATIONS.MANAGE_PAGE,
				'Error',
				'Submit Credentials',
				{ errorTitle: responseModalConfigs.GENERIC_ERROR.title },
			)
		}

		trackAnalyticEvent(
			ANALYTICS_LOCATIONS.MANAGE_PAGE,
			'Click',
			'Submit Credentials',
		)
	}

	const onSubmitFeedDescription = async() => {
		const name = formData.name !== null ? normalizePayloadString( formData.name ) : formData.name
		const response = await updateFeed( { input: { feedId: feedId!, name } } )

		if ( response.updateFeed?.error ) {
			showModal( {
				title: 'Error Updating Feed Description',
				headerText: 'There was an error updating the feed description',
				content: response.updateFeed.error.message,
			} )

			trackAnalyticEvent(
				ANALYTICS_LOCATIONS.MANAGE_PAGE,
				'Error',
				'Edit Description',
			)
		} else {
			setIsFeedDescriptionEditable( false )
			setIsFormDirty( false )
			showToast( { title: 'Feed Description Updated', message: '', type: 'success' } )

			trackAnalyticEvent(
				ANALYTICS_LOCATIONS.MANAGE_PAGE,
				'Success',
				'Edit Description',
				{ name },
			)
		}

		trackAnalyticEvent(
			ANALYTICS_LOCATIONS.MANAGE_PAGE,
			'Click',
			'Save Description',
		)
	}
	const resetForm = () => {
		setFormData( initialFormDataRef.current ?? { id: params.id, name: feedName ?? '' } )
		setIsFeedCredentialsEditable( false )
		setIsFormDirty( false )
		setFeedDescriptionError( false )
		setIsFeedDescriptionEditable( false )
	}

	const onCancelClick = () => {
		trackAnalyticEvent(
			ANALYTICS_LOCATIONS.MANAGE_PAGE,
			'Click',
			'Cancel',
		)
		if ( isFormDirty ) {
			showModal( {
				title: 'Unsaved Changes',
				headerText: 'Integration has unsaved changes',
				content: 'Changes to this integration will be lost. Are you sure you want to discard changes?',
				buttons: [
					{ label: 'Continue editing', variant: 'text', color: 'primary', onClick: () => hideModal() },
					{ label: 'Discard changes',
						variant: 'contained',
						color: 'primary',
						onClick: () => {
							resetForm()
							setIsFeedCredentialsEditable( false )
							setIsFeedDescriptionEditable( false )
						},
					},
				],
			} )
		} else {
			setIsFeedCredentialsEditable( false )
			setIsFeedDescriptionEditable( false )
		}
	}

	const isEditing = isFeedCredentialsEditable || isFeedDescriptionEditable
	const sortedRequiredFields = removeExpiresAtFromRequiredFields( sortRequiredFieldsLogically( requiredFields ?? [] ) )

	const renderFeedDescriptionSection = () => {
		return (
			<>
				<Stack direction="row" spacing={1}>
					<StyledHeaderTypography variant="h3" gutterBottom>
						Feed Description
					</StyledHeaderTypography>
					{shouldShowEditFeedDescriptionButton && <StyledButton variant="text" color="primary" aria-label="Edit Feed Description" onClick={() => setIsFeedDescriptionEditable( true )}>
						<StyledEditIcon
							aria-label="Edit Feed Description"
							onClick={() => {
								setIsFeedDescriptionEditable( true )

								trackAnalyticEvent(
									ANALYTICS_LOCATIONS.MANAGE_PAGE,
									'Click',
									'Feed Description Edit Button',
								)
							}}
						/>
					</StyledButton>}
				</Stack>
				{renderDescriptionTextInput()}
			</>
		)
	}

	return (
		<StyledForm noValidate autoComplete="off" aria-label={`manage-form-${integrationName}`} name={`manage-form-${feedId}`} editing={isEditing}>
			<FormInputContainer>
				<Stack spacing={2}>
					{renderFeedDescriptionSection()}
					<StyledCredentialsContainer style={{ marginTop: '32px' }} direction="row" spacing={1}>
						<StyledHeaderTypography variant="h3" gutterBottom>
						Integration Credentials
						</StyledHeaderTypography>
						{shouldShowEditFeedCredentialsButton && <StyledButton variant="text" color="primary" aria-label="Edit Integration Credentials" onClick={onEditCredentialsClick}>
							<StyledEditIcon aria-label="Edit Integration Credentials" onClick={onEditCredentialsClick} />
						</StyledButton>}
					</StyledCredentialsContainer>
					{isOauth ? <OAuthIntegrationManage isEditing={isFeedCredentialsEditable} /> : sortedRequiredFields.map( ( field ) => renderField( field ) )}
				</Stack>
				<DeleteButtonContainer editing={isEditing}>
					<StyledDeleteButton variant="text" color="error" onClick={onDeletePress}>
						<Typography>
							Delete Integration
						</Typography>
					</StyledDeleteButton>
				</DeleteButtonContainer>
			</FormInputContainer>
			{( isFeedCredentialsEditable || isFeedDescriptionEditable ) && ( <FooterContainer>
				<FooterButtonContainer>
					<BackLink onClick={onCancelClick}>
						<Typography variant="body1" color="primary">
						Cancel
						</Typography>
					</BackLink>
					{shouldShowSubmitFeedDescriptionButton &&
					<Button variant={isFormDirty ? 'contained' : 'outlined'} type="button" disabled={!isFormDirty || ( isFeedDescriptionEditable && feedDescriptionError )} onClick={isFeedCredentialsEditable ? onSubmitCredentials : onSubmitFeedDescription}>
						<Typography>
							{shouldShowSubmitFeedCredentialsButton ? 'Update credentials' : 'Save feed description'}
						</Typography>
					</Button>}
				</FooterButtonContainer>
			</FooterContainer> )}
		</StyledForm>
	)
}
